"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  isWorkerDownloadProvider,
  parseVideoSource,
  type VideoProvider,
} from "@ziggapp/klap-core/video";

export type VideoMetadata = {
  dimensions: { width: number; height: number } | null;
  duration: number | null;
  language: string | null;
  title: string | null;
  thumbnailUrl: string | null;
  // Definitive accessibility verdict from /video-details: "inaccessible"
  // means the link cannot be processed (private/deleted/not shared) and the
  // dialog blocks Generate; "ok"/"unknown" behave as before (the server
  // re-validates at creation).
  access: "ok" | "inaccessible" | "unknown";
  // Why an "inaccessible" link is inaccessible, when the API says.
  accessReason: VideoAccessReason | null;
};

// /video-details `access_reason`. Twitch VODs that exist but won't play are
// "not_public_yet" (usually just published: it clears on its own) or
// "subscribers_only"; everything else is "private_or_missing".
const ACCESS_REASONS = [
  "private_or_missing",
  "not_public_yet",
  "subscribers_only",
] as const;
export type VideoAccessReason = (typeof ACCESS_REASONS)[number];

export type VideoInput =
  | { type: "file"; value: File | null }
  | { type: "url"; value: string | null };

// Shared with VideoInput's paste-time prefetch: it warms the same cache
// entry this hook reads, keyed as [VIDEO_METADATA_QUERY_KEY, type, value].
export const VIDEO_METADATA_QUERY_KEY = "video-metadata";

// Provider-specific fix instructions for links whose access verdict is
// "inaccessible". Shown as a toast at paste time (VideoInput).
export const providerToInaccessibleMessage: Partial<
  Record<VideoProvider, string>
> = {
  drive:
    'This Google Drive file isn\'t shared. In Drive, set it to "Anyone with the link", then paste it again.',
  vimeo:
    "This Vimeo video is private or unavailable. Make it public or embeddable, then try again.",
  loom: 'This Loom video is private. Allow "Anyone with the link" to view it, then try again.',
  twitch: "This Twitch video was deleted or doesn't exist.",
  dropbox:
    "We couldn't access this Dropbox file. Use a shared file link (not a folder link).",
};

// Twitch VODs that exist but won't play: the API says why.
const twitchReasonToInaccessibleMessage: Partial<
  Record<VideoAccessReason, string>
> = {
  not_public_yet:
    "This Twitch VOD isn't public yet. If you just published it, try again in a few minutes.",
  subscribers_only:
    "This Twitch VOD is for subscribers only. Make it public on Twitch, or upload the video file.",
};

export const DEFAULT_INACCESSIBLE_MESSAGE =
  "We can't access this link. Check the URL and its sharing settings.";

export const getInaccessibleMessage = (
  provider: VideoProvider | undefined,
  reason?: VideoAccessReason | null
): string =>
  (provider === "twitch" && reason
    ? twitchReasonToInaccessibleMessage[reason]
    : undefined) ??
  (provider && providerToInaccessibleMessage[provider]) ??
  DEFAULT_INACCESSIBLE_MESSAGE;

const EMPTY_METADATA: VideoMetadata = {
  dimensions: null,
  duration: null,
  language: null,
  title: null,
  thumbnailUrl: null,
  access: "unknown",
  accessReason: null,
};

// Provider-agnostic display metadata from the API (fast direct HTTP per
// provider; always responds 200 with nullable fields, so this never throws
// and never triggers react-query's retry loop).
export const fetchUrlMetadata = async (url: string): Promise<VideoMetadata> => {
  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/video-details?url=${encodeURIComponent(url)}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );
    if (!res.ok) return { ...EMPTY_METADATA };

    const data = await res.json();
    return {
      dimensions: data.dimensions ?? null,
      duration: data.duration_seconds ?? null,
      language: data.language ?? null,
      title: data.title ?? null,
      thumbnailUrl: data.thumbnail_url ?? null,
      access:
        data.access === "ok" || data.access === "inaccessible"
          ? data.access
          : "unknown",
      accessReason: ACCESS_REASONS.includes(data.access_reason)
        ? data.access_reason
        : null,
    };
  } catch {
    return { ...EMPTY_METADATA };
  }
};

// Probe a video source (objectURL or remote direct URL) with a <video>
// element. Metadata properties on video elements are readable cross-origin
// without CORS headers. Resolves to nulls on any error, keeping access
// "unknown" — an element error does NOT mean the link is inaccessible (e.g.
// mkv containers don't play in browsers but process fine server-side).
const PROBE_VIDEO_ELEMENT_TIMEOUT_MS = 8_000;

const probeVideoElement = (src: string): Promise<VideoMetadata> =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;

    // A stalled host (or a non-faststart mp4 whose moov atom is at the end)
    // can leave the element loading indefinitely with neither event firing —
    // and this probe sits behind the paste-time gate, so it must resolve.
    const timeout = setTimeout(() => {
      video.src = "";
      resolve({ ...EMPTY_METADATA });
    }, PROBE_VIDEO_ELEMENT_TIMEOUT_MS);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      resolve({
        ...EMPTY_METADATA,
        dimensions: {
          width: video.videoWidth,
          height: video.videoHeight,
        },
        duration: video.duration,
      });
      video.src = "";
    };

    video.onerror = () => {
      clearTimeout(timeout);
      resolve({ ...EMPTY_METADATA });
    };

    video.src = src;
  });

// Full metadata for a pasted URL — the query fn behind the hook, exported so
// VideoInput can fetch the same cache entry (same key, same shape) at paste
// time for the accessibility gate. Never throws.
export const fetchVideoUrlMetadata = async (
  url: string
): Promise<VideoMetadata> => {
  const source = parseVideoSource(url);
  if (!source) return { ...EMPTY_METADATA };

  // dropbox/direct links have no metadata page — probe them client-side
  // with a <video> element for dimensions/duration, while the API
  // supplies the access verdict. Run both in parallel and merge.
  if (!isWorkerDownloadProvider(source.provider)) {
    const [probed, remote] = await Promise.all([
      probeVideoElement(source.fetchUrl),
      fetchUrlMetadata(url),
    ]);
    return {
      ...probed,
      access: remote.access,
      accessReason: remote.accessReason,
    };
  }

  return fetchUrlMetadata(url);
};

export const useVideoMetadata = (input: VideoInput) => {
  const [fileMetadata, setFileMetadata] = useState<VideoMetadata>({
    ...EMPTY_METADATA,
  });

  // Remote video metadata query
  const remoteQuery = useQuery({
    queryKey: [VIDEO_METADATA_QUERY_KEY, input.type, input.value],
    queryFn: async () => {
      if (input.type !== "url" || !input.value) return null;
      return fetchVideoUrlMetadata(input.value);
    },
    enabled: input.type === "url" && Boolean(input.value),
    // Metadata for a given URL doesn't change, and VideoInput prefetches
    // this entry at paste time — without a staleTime, mounting the hook
    // right after that prefetch resolves would refire the same request.
    staleTime: 5 * 60 * 1000,
  });

  // File metadata handling
  useEffect(() => {
    if (input.type !== "file" || !input.value) {
      setFileMetadata({ ...EMPTY_METADATA });
      return;
    }

    let cancelled = false;
    const objectUrl = URL.createObjectURL(input.value);

    probeVideoElement(objectUrl).then((metadata) => {
      if (!cancelled) setFileMetadata(metadata);
      URL.revokeObjectURL(objectUrl);
    });

    return () => {
      cancelled = true;
      URL.revokeObjectURL(objectUrl);
    };
  }, [input.type, input.value]);

  // Combine results based on input type
  const metadata = input.type === "file" ? fileMetadata : remoteQuery.data;
  const isLoading = input.type === "file" ? false : remoteQuery.isLoading;
  const error = input.type === "file" ? null : remoteQuery.error;

  return {
    metadata,
    isLoading,
    error,
    isError: Boolean(error),
  };
};
